import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ImageUpload } from "@/components/project-detail/camera-plans/ImageUpload";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, UploadCloud } from "lucide-react";
import { PLAYOUT_FIRST_MEDIA_SLOT } from "@production-hub/shared";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  usePlayoutAssets,
  useCreatePlayoutAsset,
  useUpdatePlayoutAsset,
  useVmixInputs,
  useDeletePlayoutAsset,
  useSyncPlayoutAsset,
  useSyncAllPlayoutAssets,
  usePlayoutConfig,
} from "@/hooks/useBridgePlayout";

interface PlayoutAssetsProps {
  bridgeUrl: string | undefined | null;
}

/** Pixel dimensions, plus duration for video, read from the file itself. */
function readMediaSize(
  file: File,
): Promise<{ width: number; height: number; duration?: number }> {
  return new Promise<{ width: number; height: number; duration?: number }>(
    (resolve, reject) => {
      const url = URL.createObjectURL(file);

      if (file.type.startsWith("video/")) {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
          resolve({
            width: video.videoWidth,
            height: video.videoHeight,
            duration: Number.isFinite(video.duration)
              ? video.duration
              : undefined,
          });
          URL.revokeObjectURL(url);
        };
        video.onerror = () => reject(new Error("Could not read video"));
        video.src = url;
        return;
      }

      const image = new Image();
      image.onload = () => {
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
        URL.revokeObjectURL(url);
      };
      image.onerror = () => reject(new Error("Could not read image"));
      image.src = url;
    },
  );
}

/** "18.5s" / "1:02" */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1).replace(/\.0$/, "")}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;
}

export function PlayoutAssets({ bridgeUrl }: PlayoutAssetsProps) {
  const { data: assets = [] } = usePlayoutAssets(bridgeUrl);
  const { data: configData } = usePlayoutConfig(bridgeUrl);
  const createAsset = useCreatePlayoutAsset(bridgeUrl);
  const updateAsset = useUpdatePlayoutAsset(bridgeUrl);
  const { data: vmix } = useVmixInputs(bridgeUrl, true);
  const deleteAsset = useDeletePlayoutAsset(bridgeUrl);
  const syncAsset = useSyncPlayoutAsset(bridgeUrl);
  const syncAll = useSyncAllPlayoutAssets(bridgeUrl);

  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);

  const videoMode = configData?.video_mode;

  const uploadOne = async (file: File) => {
    const isVideo = file.type.startsWith("video/");
    const { width, height, duration } = await readMediaSize(file);

    // Stills go to the ATEM media pool and must match the video mode. Videos
    // are played out by vMix, so their size only matters for the preview.
    if (
      !isVideo &&
      videoMode &&
      (width !== videoMode.width || height !== videoMode.height)
    ) {
      toast.warning(
        `Artwork is ${width}×${height} but the switcher runs ${videoMode.width}×${videoMode.height}. It will be resized on sync.`,
      );
    }
    if (Math.abs(width / height - 16 / 9) > 0.01) {
      toast.warning("Artwork is not 16:9 and may be distorted.");
    }

    const response = await fetch("/api/storage/upload", {
      method: "POST",
      headers: { "Content-Type": file.type, "x-file-name": file.name },
      body: file,
    });
    if (!response.ok) throw new Error("Upload failed");
    const { publicUrl } = await response.json();

    await createAsset.mutateAsync({
      name: name || file.name,
      media_type: isVideo ? "video" : "still",
      // Refreshed from vMix once an input is assigned; vMix is authoritative
      // because that is the copy which actually plays.
      duration_seconds: duration,
      image_url: publicUrl,
      width,
      height,
    });
    setName("");
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      await uploadOne(file);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to upload artwork",
      );
    } finally {
      setUploading(false);
    }
  };

  /**
   * Uploaded one at a time rather than in parallel: each one hits S3 and then
   * the bridge, and a failure part-way should not leave the rest in limbo.
   */
  const handleUploadFiles = async (files: File[]) => {
    setUploading(true);
    let done = 0;
    try {
      for (const file of files) {
        await uploadOne(file);
        done += 1;
      }
      if (files.length > 1) toast.success(`Uploaded ${done} files`);
    } catch (error) {
      toast.error(
        `${error instanceof Error ? error.message : "Upload failed"}` +
          (done ? ` (${done} of ${files.length} uploaded)` : ""),
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Artwork</CardTitle>
        <CardDescription>
          Stills sync into the ATEM media pool from slot{" "}
          {PLAYOUT_FIRST_MEDIA_SLOT} upward — slots 1–
          {PLAYOUT_FIRST_MEDIA_SLOT - 1} are left to the studio. Videos are
          played out by vMix and are never uploaded; the copy here only gives
          the editor something to position against.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Name (optional)</Label>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Sponsor name"
          />
        </div>

        <ImageUpload
          onUpload={handleUpload}
          onUploadFiles={handleUploadFiles}
          multiple
          isUploading={uploading}
          accept="image/*,video/*"
          hint="PNG or JPG for L-shapes, MP4 for video cut-ins — drop several at once"
        />

        {assets.length > 0 && (
          <>
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => syncAll.mutate()}
                disabled={syncAll.isPending}
              >
                <UploadCloud className="mr-1 h-3 w-3" />
                Sync all to switcher
              </Button>
            </div>

            <div className="space-y-2">
              {assets.map((asset) => (
                <div
                  key={asset.id}
                  className="flex items-center gap-3 rounded-md border p-2"
                  data-testid="playout-asset"
                >
                  {asset.media_type === "video" ? (
                    <video
                      src={asset.image_url}
                      className="h-12 w-20 rounded bg-muted object-contain"
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={asset.image_url}
                      alt={asset.name}
                      className="h-12 w-20 rounded object-cover"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{asset.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {asset.width}×{asset.height}
                      {asset.duration_seconds !== undefined &&
                        ` · ${formatDuration(asset.duration_seconds)}`}
                      {asset.hold_override_seconds !== undefined &&
                        ` · held ${formatDuration(asset.hold_override_seconds)}`}
                    </div>
                  </div>
                  {asset.media_type === "video" && (
                    <div className="w-28">
                      <Input
                        type="number"
                        min={0}
                        step={0.5}
                        defaultValue={asset.hold_override_seconds ?? ""}
                        placeholder="Hold (s)"
                        title="Override how long this clip stays on air. Defaults to its own duration."
                        data-testid="asset-hold-override"
                        onBlur={(event) => {
                          const raw = event.target.value.trim();
                          const value = raw === "" ? null : Number(raw);
                          if (value !== (asset.hold_override_seconds ?? null)) {
                            updateAsset.mutate({
                              id: asset.id,
                              data: { hold_override_seconds: value },
                            });
                          }
                        }}
                      />
                    </div>
                  )}
                  {asset.media_type === "video" ? (
                    // The video lives on vMix, so the input number belongs to
                    // the asset - every playout using it shares the same input.
                    <div className="w-56">
                      {vmix?.inputs?.length ? (
                        <Select
                          value={
                            asset.vmix_input !== undefined
                              ? String(asset.vmix_input)
                              : ""
                          }
                          onValueChange={(value) =>
                            updateAsset.mutate({
                              id: asset.id,
                              data: { vmix_input: Number(value) },
                            })
                          }
                        >
                          <SelectTrigger data-testid="asset-vmix-input">
                            <SelectValue placeholder="Set vMix input" />
                          </SelectTrigger>
                          <SelectContent>
                            {vmix.inputs.map((input) => (
                              <SelectItem
                                key={input.number}
                                value={String(input.number)}
                              >
                                {input.number}. {input.title || input.type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type="number"
                          min={1}
                          defaultValue={asset.vmix_input ?? ""}
                          placeholder="vMix input"
                          data-testid="asset-vmix-input"
                          onBlur={(event) => {
                            const value = Number(event.target.value);
                            if (value && value !== asset.vmix_input) {
                              updateAsset.mutate({
                                id: asset.id,
                                data: { vmix_input: value },
                              });
                            }
                          }}
                        />
                      )}
                    </div>
                  ) : asset.media_slot !== undefined ? (
                    <Badge variant="secondary">Slot {asset.media_slot}</Badge>
                  ) : (
                    <Badge variant="outline">Not synced</Badge>
                  )}
                  {asset.media_type !== "video" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => syncAsset.mutate(asset.id)}
                      disabled={syncAsset.isPending}
                      title="Upload to ATEM media pool"
                    >
                      <UploadCloud className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteAsset.mutate(asset.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
